// This file exists to avoid a circular dependency error when running tests

import { createSelector } from 'reselect';
import { Map } from 'immutable';
import { NO_OVERHANG_ID, WATERFALL_ID_45_DEGREES, WATERFALL_WITH_OVERHANG_ID_45_DEGREES, WATERFALL_ID_90_DEGREES, WATERFALL_WITH_OVERHANG_ID_90_DEGREES } from 'domain/Options/generators/overhangs';
import { calcOverhangMeasurementsError, calcIrregularMeasurementsError } from 'pages/MainPage/PlanPage/overhangMeasurementsError';
import { measurementsLimitSelector } from 'domain/Options/selectors/measurements';
import { convertToImperial } from 'domain/Options/utils/convertMeasurement';
import planPageMessages from 'pages/MainPage/PlanPage/messages';
import { shapeIdSelector, kitchenIslandIdSelector } from 'domain/Options/selectors/utils';
import { overhangFieldsSelector, kitchenIslandOverhangFieldsSelector } from './input';
import {
  overhangSideParameterSelector,
  overhangParameterSelector,
  plannedShapeParameterSelector,
  selectedThicknessSelector,
  thicknessParameterSelector,
  kitchenIslandThicknessParameterSelector,
  kitchenIslandOverhangParameterSelector,
  selectedKitchenIslandThicknessSelector,
  kitchenIslandWithMeasurementsSelector,
  kitchenIslandOverhangSideParameterSelector,
} from './parameters';
import { configurationSelector } from '../selectors';
import { KITCHEN_ISLAND_ID } from '../generators/kitchenIslands';

const makeOverhangSideMeasurementsSelector = (fieldsSelector, shapeSelector, overhangSelector, selectedThicknessSel, thicknessParameterSel) => createSelector(
  fieldsSelector,
  shapeSelector,
  overhangSelector,
  selectedThicknessSel,
  thicknessParameterSel,
  configurationSelector,
  (overhangFields, shapeId, overhang, selectedThickness, thicknessParameter, configuration) => {
    return (overhangSideOption) => {
      const overhangId = overhang.getIn(['value', 'id']);
      if (!overhangSideOption || !overhang || overhangId === NO_OVERHANG_ID) {
        return undefined;
      }
      const thickness = selectedThickness ? selectedThickness.get('thickness') : parseInt(thicknessParameter.getIn(['value', 'id']));
      const waterfallMeasurement = (overhangId === WATERFALL_ID_45_DEGREES || overhangId === WATERFALL_ID_90_DEGREES) && thickness;
      const sideId = overhangSideOption.get('id');
      const dimensions = overhangSideOption.get('dimensions');

      let selectedOverhangFields;
      if (shapeId === KITCHEN_ISLAND_ID) {
        selectedOverhangFields = (overhangFields[overhangId] && overhangFields[overhangId][sideId]) || {};
      } else {
        selectedOverhangFields = (overhangFields[shapeId] && overhangFields[shapeId][overhangId] && overhangFields[shapeId][overhangId][sideId]) || {};
      }

      return Map(dimensions.reduce((accumulated, ignored, field) => {
        return {
          ...accumulated,
          [field]: waterfallMeasurement || selectedOverhangFields[field] || configuration.get('overhangDefaultSize'),
        };
      }, {}));
    };
  }
);

export const overhangSideMeasurementsSelector = makeOverhangSideMeasurementsSelector(overhangFieldsSelector, shapeIdSelector, overhangParameterSelector, selectedThicknessSelector, thicknessParameterSelector);
export const kitchenIslandOverhangSideMeasurementsSelector = makeOverhangSideMeasurementsSelector(kitchenIslandOverhangFieldsSelector, kitchenIslandIdSelector, kitchenIslandOverhangParameterSelector, selectedKitchenIslandThicknessSelector, kitchenIslandThicknessParameterSelector);


export const makeWorktopWithOverhangMeasurementsSelector = (shapeSelector, sideSelector, parameterSelector) => createSelector(
  shapeSelector,
  sideSelector,
  parameterSelector,
  (shape, getOverhangSideMeasurements, overhang) => {
    return (overhangSideOption) => {
      const overhangId = overhang.getIn(['value', 'id']);

      if (overhangId === NO_OVERHANG_ID) return shape.get('measurements');
      if (overhangSideOption !== null) {
        const overhangMeasurements = getOverhangSideMeasurements(overhangSideOption);
        const overhangDimensions = overhangSideOption.get('dimensions');

        return shape.get('measurements').withMutations((tmp) =>
          overhangDimensions.reduce((result, dimension, dimensionKey) =>
          result.set(dimension.get('length'), result.get(dimension.get('length')) + overhangMeasurements.get(dimensionKey))
          , tmp)
        );
      }
    };
  }
);

export const worktopWithOverhangMeasurementsSelector = makeWorktopWithOverhangMeasurementsSelector(plannedShapeParameterSelector, overhangSideMeasurementsSelector, overhangParameterSelector);
export const kitchenIslandWithOverhangMeasurementsSelector = makeWorktopWithOverhangMeasurementsSelector(kitchenIslandWithMeasurementsSelector, kitchenIslandOverhangSideMeasurementsSelector, kitchenIslandOverhangParameterSelector);

export const getImperialFields = (shapeId, shapeList, measurementswithRnd, measurementsWithOverhangWithRnd) => {
  let fieldValuesWithOverHangNoRounding;
  if (shapeList[shapeId]) {
    fieldValuesWithOverHangNoRounding = JSON.parse(JSON.stringify(shapeList[shapeId]));
    for (let i = 0; i < Object.keys(fieldValuesWithOverHangNoRounding).length; i++) {
      const fieldName = Object.keys(fieldValuesWithOverHangNoRounding)[i];
      const wktpWithoutOverhang = measurementswithRnd.get(fieldName);
      const wktpWithOverhang = Object.values(measurementsWithOverhangWithRnd)[i];
      const overHang = wktpWithOverhang - wktpWithoutOverhang;
      const wktpWithoutOverhangNoRounding = fieldValuesWithOverHangNoRounding[fieldName];
      fieldValuesWithOverHangNoRounding[fieldName] = wktpWithoutOverhangNoRounding + overHang;
    }
  }
  return fieldValuesWithOverHangNoRounding;
};

const makeOverhangMeasurementErrorSelector = (shapeSelector, fieldsSelector, overhangSelector, worktopWithOverhangMeasurementsSel, limitSelector, thicknessSelector, thicknessParameterSel) => createSelector(
  shapeSelector,
  fieldsSelector,
  overhangSelector,
  worktopWithOverhangMeasurementsSel,
  limitSelector,
  thicknessSelector,
  thicknessParameterSel,
  configurationSelector,
  (shape, overhangFields, overhang, getWorktopWithOverhangMeasurements, limits, selectedThickness, thicknessParameter, configuration) => {
    const shapeId = shape.getIn(['value', 'id']);
    const overhangId = overhang.getIn(['value', 'id']);
    const usingImperial = configuration.get('useImperialUnitMeasurementsInTheUI');
    let minLength;
    if (overhangId === WATERFALL_WITH_OVERHANG_ID_45_DEGREES || overhangId === WATERFALL_WITH_OVERHANG_ID_90_DEGREES) {
      minLength = (selectedThickness && selectedThickness.get('thickness')) || thicknessParameter.getIn(['value', 'id']);
    } else {
      minLength = overhang.getIn(['value', 'minLength']);
    }

    let dimensions;
    if (shapeId === KITCHEN_ISLAND_ID) {
      dimensions = overhangFields;
    } else {
      dimensions = overhangFields[shapeId];
    }
    return (overhangSideOption) => {
      if (overhangSideOption !== null) {
        let measurementsWithWorktop = getWorktopWithOverhangMeasurements(overhangSideOption).toJS();
        const maxLength = limits.length.max;
        const maxDepth = limits.depth.max;
        const minDepth = limits.depth.min;
        const overhangSideId = overhangSideOption.get('id');
        const overhangSideDefaults = overhangSideOption.get('dimensions').reduce((result, dimension, dimensionName) => {
          return {
            ...result,
            [dimensionName]: configuration.get('overhangDefaultSize'),
          };
        }, {});

        const overhangSideDimensions = (dimensions && dimensions[overhangId] && dimensions[overhangId][overhangSideId]) || overhangSideDefaults;
        if (shapeId === 'irregular') {
          const shapeMeasurements = shape.get('measurements');
          return calcIrregularMeasurementsError(
            shapeMeasurements,
            overhangSideOption,
            overhangSideDimensions,
            measurementsWithWorktop,
            minDepth,
            maxDepth,
            minLength,
            maxLength,
            configuration.get('overhangDefaultSize'),
            configuration.get('useImperialUnitMeasurementsInTheUI'),
          );
        }
        return calcOverhangMeasurementsError(overhangSideDimensions, measurementsWithWorktop, minLength, maxLength, overhangSideOption, shape, configuration.get('useImperialUnitMeasurementsInTheUI'));
      }
    };
  }
);

export const overhangMeasurementErrorSelector = makeOverhangMeasurementErrorSelector(
  plannedShapeParameterSelector,
  overhangFieldsSelector,
  overhangParameterSelector,
  worktopWithOverhangMeasurementsSelector,
  measurementsLimitSelector,
  selectedThicknessSelector,
  thicknessParameterSelector
);

export const kitchenIslandOverhangMeasurementErrorSelector = makeOverhangMeasurementErrorSelector(
  kitchenIslandWithMeasurementsSelector,
  kitchenIslandOverhangFieldsSelector,
  kitchenIslandOverhangParameterSelector,
  kitchenIslandWithOverhangMeasurementsSelector,
  measurementsLimitSelector,
  selectedKitchenIslandThicknessSelector,
  kitchenIslandThicknessParameterSelector,
);

export const makeOverhangInvalidSelector = (...selectors) => createSelector(
    selectors,
    (overhang, overhangSide, overhangMeasurementsError) => {
      if (overhang.getIn(['value', 'id']) === NO_OVERHANG_ID) {
        return false;
      }
      return overhangMeasurementsError(overhangSide.get('value')).length > 0;
    }
);


export const overhangLegRequirementsSelector = createSelector(
  configurationSelector,
  overhangSideParameterSelector,
  overhangSideMeasurementsSelector,
  (configuration, overhangSide, overhangSideMeasurements) => {
    if (!configuration.get('includeOverhangs')) {
      return null;
    }

    const usingImperial = configuration.get('useImperialUnitMeasurementsInTheUI');
    const measurements = overhangSideMeasurements(overhangSide.get('value'));
    const overhangLegRequirements = configuration.get('overhangLegRequirements');
    const first = overhangLegRequirements.get('first');
    const repeat = overhangLegRequirements.get('repeat');
    let tempFirst;
    let tempRepeat;
    let convertedMessage;
    if (configuration.get('useImperialUnitMeasurementsInTheUI')) {
      tempFirst = parseInt(convertToImperial(first));
      tempRepeat = parseInt(convertToImperial(repeat));
      convertedMessage = planPageMessages.overhangLegRequirement.defaultMessage.toString();
      convertedMessage = convertedMessage.replace('mm', '"');
      planPageMessages.overhangLegRequirement.defaultMessage = convertedMessage;
    } else {
      tempFirst = first;
      tempRepeat = repeat;
    }
    const firstVal = tempFirst;
    const repeatVal = tempRepeat;

    return measurements && measurements.some((m) => (m > first)) ? {
      message: {
        ...(
          usingImperial
          ? planPageMessages.overhangLegRequirementImperial
          : planPageMessages.overhangLegRequirement
        ),
        values: {
          first: firstVal,
          repeat: repeatVal,
        },
      },
    } : null;
  }
);

export const overhangInvalidSelector = makeOverhangInvalidSelector(overhangParameterSelector, overhangSideParameterSelector, overhangMeasurementErrorSelector);
export const kitchenIslandOverhangInvalidSelector = makeOverhangInvalidSelector(kitchenIslandOverhangParameterSelector, kitchenIslandOverhangSideParameterSelector, kitchenIslandOverhangMeasurementErrorSelector);
